import { useEffect, useState } from "react";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { Link } from "react-router-dom";
import { UtensilsCrossed, ArrowRight, Check, TrendingUp, AlertCircle, X, Droplets, Share2 } from "lucide-react";
import SocialShareSheet from "@/components/SocialShareSheet";
import PlateStoryCard from "@/components/PlateStoryCard";

const COLORS = { veg: "#7AA37F", protein: "#C97B5B", carbs: "#E8B25E", fats: "#8DB38B" };
const VERDICT = {
  excellent: { icon: Check,       fill: "#7AA37F", tone: "bg-[#7AA37F]/12 text-[#5D8863]" },
  good:      { icon: TrendingUp,  fill: "#5A8B8F", tone: "bg-[#5A8B8F]/12 text-[#2C5254]" },
  adjust:    { icon: AlertCircle, fill: "#E8951C", tone: "bg-[#E8951C]/15 text-[#A65E00]" },
};

function verdictOf(p) {
  if (!p) return null;
  const dev = Math.max(
    Math.abs(p.veg_pct - 50),
    Math.abs(p.protein_pct - 25),
    Math.abs(p.carbs_pct - 25),
  );
  if (dev <= 10) return "excellent";
  if (dev <= 20) return "good";
  return "adjust";
}

function tipKeyOf(p) {
  if (!p) return null;
  const dv = p.veg_pct - 50, dp = p.protein_pct - 25, dc = p.carbs_pct - 25;
  if (dv < -10) return "more_veg";
  if (dp < -10) return "more_protein";
  if (dc > 15)  return "less_carbs";
  if (dp > 20)  return "less_protein";
  if (Math.max(Math.abs(dv), Math.abs(dp), Math.abs(dc)) > 10) return "rebalance";
  return null;
}

export default function PlateTodayCard({ date }) {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightOpen, setInsightOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    if (reloadKey === 0) setLoading(true);
    const url = date
      ? `/client/plate-history?date=${encodeURIComponent(date)}`
      : "/client/plate-history?days=1";
    api.get(url)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date, reloadKey]);

  // Label switches when the user navigates away from today
  const titleLabel = (() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    if (!date || date === todayIso) return t("plateToday.title");
    try {
      const d = new Date(date + "T12:00:00");
      const nice = new Intl.DateTimeFormat(lang || "it", { weekday: "short", day: "numeric", month: "short" }).format(d);
      return `${t("plateToday.plateOn")} ${nice}`;
    } catch {
      return t("plateToday.title");
    }
  })();

  if (loading) {
    return (
      <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-4 md:p-5 mb-4 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-slate-100 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-24 bg-slate-100 rounded" />
            <div className="h-4 w-40 bg-slate-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const today = (data?.series || [])[0];
  const plate = today?.plate;
  const verdict = verdictOf(plate);
  const V = verdict ? VERDICT[verdict] : null;
  const Icon = V ? V.icon : UtensilsCrossed;
  const tipKey = tipKeyOf(plate);

  // Donut math
  const R = 22, C = 2 * Math.PI * R;
  const arcs = plate
    ? [
        { pct: plate.veg_pct,     color: COLORS.veg },
        { pct: plate.protein_pct, color: COLORS.protein },
        { pct: plate.carbs_pct,   color: COLORS.carbs },
      ]
    : [];
  let acc = 0;

  return (
    <>
    <button
      type="button"
      onClick={() => setInsightOpen(true)}
      className="block w-full text-left mb-4 rounded-3xl bg-white border border-slate-100 shadow-sm p-4 md:p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
      data-testid="plate-today-card"
    >
      <div className="flex items-center gap-4">
        {/* Donut / empty ring */}
        <div className="relative shrink-0" style={{ width: 64, height: 64 }}>
          <svg viewBox="0 0 60 60" width={64} height={64} aria-hidden>
            <circle cx="30" cy="30" r={R} fill="none" stroke="#f1f5f9" strokeWidth="7" />
            {arcs.map((a, i) => {
              if (a.pct <= 0) return null;
              const len = (a.pct / 100) * C;
              const off = C - (acc / 100) * C;
              acc += a.pct;
              return (
                <circle
                  key={i}
                  cx="30" cy="30" r={R}
                  fill="none"
                  stroke={a.color}
                  strokeWidth="7"
                  strokeDasharray={`${len} ${C - len}`}
                  strokeDashoffset={off}
                  transform="rotate(-90 30 30)"
                />
              );
            })}
          </svg>
          {/* Center icon */}
          <span
            className="absolute inset-0 flex items-center justify-center"
            style={{ color: V ? V.fill : "#94a3b8" }}
          >
            <Icon className="h-4 w-4" />
          </span>
        </div>

        {/* Text block */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
              {titleLabel}
            </span>
            {plate && verdict && (
              <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${V.tone}`}>
                {t(`plateCheck.verdict.${verdict}`)}
              </span>
            )}
          </div>

          {plate ? (
            <>
              <div className="mt-1 flex items-center gap-2 tabular-nums" data-testid="plate-today-pcts">
                <span className="font-heading text-base md:text-lg font-semibold" style={{ color: COLORS.veg }}>{plate.veg_pct}%</span>
                <span className="text-slate-300">·</span>
                <span className="font-heading text-base md:text-lg font-semibold" style={{ color: COLORS.protein }}>{plate.protein_pct}%</span>
                <span className="text-slate-300">·</span>
                <span className="font-heading text-base md:text-lg font-semibold" style={{ color: COLORS.carbs }}>{plate.carbs_pct}%</span>
                <span className="text-[11px] text-slate-400 ml-1">/ 50 · 25 · 25</span>
                {plate.healthy_fats && (
                  <span data-testid="plate-today-fats"
                    className={`ml-auto inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${plate.healthy_fats.present ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    <Droplets className="h-3 w-3" /> {plate.healthy_fats.present ? "✓" : "–"}
                  </span>
                )}
              </div>
              {tipKey && (
                <p className="mt-0.5 text-xs text-slate-600 leading-snug line-clamp-1">
                  {t(`plateCheck.tip.${tipKey}`)}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="mt-1 font-heading text-base font-semibold text-slate-800">
                {t("plateToday.empty")}
              </p>
              <p className="text-xs text-slate-500 leading-snug">
                {t("plateToday.emptyHint")}
              </p>
            </>
          )}
        </div>

        <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
      </div>
    </button>

    {insightOpen && (
      <PlateInsightSheet
        plate={plate}
        verdict={verdict}
        titleLabel={titleLabel}
        t={t}
        date={date || new Date().toISOString().slice(0, 10)}
        onSaved={() => setReloadKey((k) => k + 1)}
        onShare={() => { setInsightOpen(false); setShareOpen(true); }}
        onClose={() => setInsightOpen(false)}
      />
    )}
    {plate && (
      <SocialShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        lang={lang}
        t={t}
        formats={["story"]}
        mascot="solano.png"
        title={t("plateToday.shareTitle")}
        slug={`teller-${date || "today"}`}
        renderKey={`${date}-${plate.veg_pct}-${plate.protein_pct}-${plate.carbs_pct}`}
        captionPayload={{
          kind: "plate", veg_pct: plate.veg_pct, protein_pct: plate.protein_pct, carbs_pct: plate.carbs_pct,
          verdict, healthy_fats: !!plate.healthy_fats?.present, tip: tipKey ? t(`plateCheck.tip.${tipKey}`) : "",
        }}
        renderCard={(format, assets) => (
          <PlateStoryCard
            plate={plate}
            verdict={verdict}
            assets={assets}
            dateLabel={(() => { try { return new Intl.DateTimeFormat(lang || "de", { weekday: "short", day: "numeric", month: "short" }).format(new Date((date || new Date().toISOString().slice(0, 10)) + "T12:00:00")); } catch { return ""; } })()}
            labels={{
              kicker: t("plateToday.storyKicker"),
              title: t("plateCheck.title"),
              verdict: verdict ? t(`plateCheck.verdict.${verdict}`) : "",
              targetLine: `${t("plateToday.target")} 50 · 25 · 25`,
              target: t("plateToday.target"),
              slices: { veg: t("plateCheck.slice.veg"), protein: t("plateCheck.slice.protein"), carbs: t("plateCheck.slice.carbs") },
              fats: t("plateCheck.slice.fats"),
              fatsMissing: t("plateCheck.fatsMissing"),
              tipKicker: t("plateToday.tomorrow"),
              tip: tipKey ? t(`plateCheck.tip.${tipKey}`) : t("plateToday.keepUp"),
              madeWith: t("recipes.socialMadeWith"),
            }}
          />
        )}
      />
    )}
    </>
  );
}

// Bottom sheet: how today went + concrete "do better tomorrow" tips.
function PlateInsightSheet({ plate, verdict, titleLabel, t, date, onSaved, onShare, onClose }) {
  const [edit, setEdit] = useState(null);
  const [savingOv, setSavingOv] = useState(false);
  const startEdit = () => setEdit((plate?.items || []).map((it) => ({
    name: it.name,
    grams: it.grams != null ? String(it.grams) : "",
    slice: it.slice === "fats" ? "other" : (it.slice || "unknown"),
  })));
  const saveOverrides = async () => {
    setSavingOv(true);
    try {
      await api.post("/client/plate-overrides", {
        date,
        items: edit.map((it) => ({
          name: it.name,
          slice: it.slice === "unknown" ? "other" : it.slice,
          grams: parseFloat(String(it.grams).replace(",", ".")) || null,
        })),
      });
      setEdit(null);
      onSaved?.();
    } catch { /* keep editing */ }
    finally { setSavingOv(false); }
  };
  const rows = plate ? [
    { label: t("plateCheck.slice.veg"), pct: plate.veg_pct, target: 50, color: COLORS.veg },
    { label: t("plateCheck.slice.protein"), pct: plate.protein_pct, target: 25, color: COLORS.protein },
    { label: t("plateCheck.slice.carbs"), pct: plate.carbs_pct, target: 25, color: COLORS.carbs },
  ] : [];
  const tips = [];
  if (plate) {
    const dv = plate.veg_pct - 50, dp = plate.protein_pct - 25, dc = plate.carbs_pct - 25;
    if (dv < -10) tips.push(t("plateCheck.tip.more_veg"));
    if (dp < -10) tips.push(t("plateCheck.tip.more_protein"));
    if (dc > 15)  tips.push(t("plateCheck.tip.less_carbs"));
    if (dp > 20)  tips.push(t("plateCheck.tip.less_protein"));
    if (!tips.length && Math.max(Math.abs(dv), Math.abs(dp), Math.abs(dc)) > 10) tips.push(t("plateCheck.tip.rebalance"));
    if (plate.healthy_fats && !plate.healthy_fats.present) tips.push(t("plateCheck.tip.add_fats"));
    if (plate.sat_fats?.flag) tips.push(t("plateCheck.tip.swap_fats"));
  }
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center" data-testid="plate-insight-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="plate-insight-backdrop" />
      <div className="relative w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl"
           style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)" }}>
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-200" />
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{titleLabel}</p>
          <div className="flex items-center gap-1">
            {plate && (
              <button type="button" onClick={onShare} data-testid="plate-share-story-btn"
                      className="inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-2.5 py-1 text-[11px] font-semibold hover:opacity-90 transition">
                <Share2 className="h-3 w-3" /> Story
              </button>
            )}
            <button type="button" onClick={onClose} aria-label="close" data-testid="plate-insight-close"
                    className="text-slate-400 hover:text-slate-600 p-1"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {plate ? (
          <>
            {verdict && (
              <p className="font-heading text-lg font-semibold text-[#1A1D1E]">
                {t(`plateCheck.verdict.${verdict}`)} — {t(`plateToday.summary.${verdict}`)}
              </p>
            )}
            <div className="mt-4 space-y-3">
              {rows.map((r) => {
                const delta = r.pct - r.target;
                const notDetected = r.pct === 0;
                return (
                  <div key={r.label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-semibold text-slate-700">{r.label}</span>
                      {notDetected ? (
                        <span className="font-semibold text-amber-600" data-testid="plate-slice-not-detected">
                          {t("plateCheck.notDetected")}
                        </span>
                      ) : (
                        <span className="tabular-nums text-slate-500">
                          <b style={{ color: r.color }}>{r.pct}%</b> / {t("plateToday.target")} {r.target}%
                          <span className={`ml-1.5 font-semibold ${Math.abs(delta) <= 10 ? "text-emerald-600" : "text-amber-600"}`}>
                            {delta > 0 ? `+${delta}` : delta}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.min(100, r.pct)}%`, background: r.color }} />
                      <div className="absolute inset-y-0 w-0.5 bg-slate-400/70" style={{ left: `${r.target}%` }} />
                    </div>
                  </div>
                );
              })}
              {plate.healthy_fats && (
                <div data-testid="plate-fats-row">
                  <div className="flex items-center justify-between text-xs mb-1 gap-2">
                    <span className="font-semibold text-slate-700 inline-flex items-center gap-1">
                      <Droplets className="h-3.5 w-3.5" style={{ color: COLORS.fats }} /> {t("plateCheck.slice.fats")}
                    </span>
                    {plate.healthy_fats.present ? (
                      <span className="font-semibold text-emerald-600 truncate" data-testid="plate-fats-present">
                        ✓ {plate.healthy_fats.items.join(", ")}
                      </span>
                    ) : (
                      <span className="font-semibold text-amber-600 text-right" data-testid="plate-fats-missing">
                        {t("plateCheck.fatsMissing")}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 leading-snug">{t("plateCheck.fatsHint")}</p>
                </div>
              )}
              {plate.sat_fats?.flag && (
                <div className="rounded-xl bg-amber-50/80 border border-amber-100 px-3 py-2 flex items-start gap-2" data-testid="plate-satfat-hint">
                  <AlertCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-amber-800 leading-snug">
                    <b>{t("plateCheck.satFatTitle")}</b> {plate.sat_fats.items.join(", ")} — {t("plateCheck.satFatHint")}
                  </p>
                </div>
              )}
            </div>
            {/* Which food went into which slice — transparency for the user. */}
            {Array.isArray(plate.items) && plate.items.length > 0 && (
              <div className="mt-5" data-testid="plate-assignments">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                    {t("plateCheck.assigned")}
                  </p>
                  {!edit && (
                    <button
                      type="button"
                      onClick={startEdit}
                      data-testid="plate-assign-edit"
                      className="text-[11px] font-semibold text-[#2C5254] hover:underline"
                    >
                      {t("plateCheck.editAssign")}
                    </button>
                  )}
                </div>
                {edit ? (
                  <div className="space-y-2" data-testid="plate-assign-editor">
                    {edit.map((it, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="flex-1 min-w-0 text-xs text-slate-700 truncate">{it.name}</span>
                        <input
                          type="number" inputMode="numeric" min={0} max={3000}
                          value={it.grams}
                          onChange={(e) => setEdit((list) => list.map((x, j) => j === i ? { ...x, grams: e.target.value } : x))}
                          data-testid={`plate-edit-grams-${i}`}
                          className="w-16 h-8 rounded-lg border border-slate-200 px-1.5 text-xs tabular-nums text-right focus:outline-none focus:border-[#5A8B8F]"
                        />
                        <span className="text-[10px] text-slate-400">g</span>
                        <select
                          value={it.slice}
                          onChange={(e) => setEdit((list) => list.map((x, j) => j === i ? { ...x, slice: e.target.value } : x))}
                          data-testid={`plate-edit-slice-${i}`}
                          className="h-8 rounded-lg border border-slate-200 bg-white px-1 text-xs focus:outline-none focus:border-[#5A8B8F]"
                        >
                          <option value="veg">{t("plateCheck.slice.veg")}</option>
                          <option value="protein">{t("plateCheck.slice.protein")}</option>
                          <option value="carbs">{t("plateCheck.slice.carbs")}</option>
                          <option value="fats">{t("plateCheck.slice.fats")}</option>
                          <option value="other">{t("plateCheck.extras")}</option>
                          {it.slice === "unknown" && <option value="unknown">{t("plateCheck.unknown")}</option>}
                        </select>
                      </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button" onClick={() => setEdit(null)}
                        data-testid="plate-assign-cancel"
                        className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 text-xs"
                      >
                        {t("common.cancel") || "Abbrechen"}
                      </button>
                      <button
                        type="button" onClick={saveOverrides} disabled={savingOv}
                        data-testid="plate-assign-save"
                        className="h-9 px-4 rounded-lg bg-[#2C5254] hover:bg-[#234345] text-white text-xs font-semibold disabled:opacity-50"
                      >
                        {savingOv ? "…" : (t("common.save") || "Speichern")}
                      </button>
                    </div>
                  </div>
                ) : (
                <div className="space-y-1">
                  {[
                    { key: "veg", label: t("plateCheck.slice.veg"), color: COLORS.veg },
                    { key: "protein", label: t("plateCheck.slice.protein"), color: COLORS.protein },
                    { key: "carbs", label: t("plateCheck.slice.carbs"), color: COLORS.carbs },
                    { key: "fats", label: t("plateCheck.slice.fats"), color: COLORS.fats },
                    { key: "other", label: t("plateCheck.extras"), color: "#94a3b8" },
                    { key: "unknown", label: t("plateCheck.unknown"), color: "#f59e0b" },
                  ].map(({ key, label, color }) => {
                    const names = plate.items.filter((it) => (it.slice === key) || (key === "other" && it.slice === "fats")).map((it) => it.name);
                    if (!names.length) return null;
                    return (
                      <p key={key} className="text-xs text-slate-600" data-testid={`plate-assign-${key}`}>
                        <span className="inline-block h-2 w-2 rounded-full mr-1.5 align-middle" style={{ background: color }} />
                        <span className="font-semibold text-slate-700">{label}:</span> {names.join(", ")}
                      </p>
                    );
                  })}
                </div>
                )}
              </div>
            )}
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
                {t("plateToday.tomorrow")}
              </p>
              {tips.length ? (
                <ul className="space-y-1.5" data-testid="plate-insight-tips">
                  {tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#E8951C] shrink-0" />{tip}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="plate-insight-keepup">
                  <Check className="h-4 w-4" /> {t("plateToday.keepUp")}
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-slate-600">{t("plateToday.emptyHint")}</p>
        )}

        <Link
          to="/app/piatto-solano"
          onClick={onClose}
          data-testid="plate-insight-details-link"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white text-sm font-semibold h-11 transition"
        >
          {t("plateToday.detailsLink")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
